import React, { useState } from 'react';
import { ArrowRight, Mail, Phone, MapPin, CheckCircle, Globe, ExternalLink } from 'lucide-react';
import { SITE_CONTACT } from '../data/siteData';
import { dispatchFormNotification } from '../utils/mailer';

interface FooterProps {
  onNavigate: (view: string, targetId?: string) => void;
  onOpenPortal: () => void;
  onOpenBooking: () => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigate, onOpenPortal, onOpenBooking }) => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);

  const [honeypot, setHoneypot] = useState('');

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot.trim() !== '') return;
    if (!newsletterEmail.trim()) return;
    setNewsletterSubscribed(true);

    dispatchFormNotification({
      subject: `[Newsletter Subscriber] ${newsletterEmail}`,
      message: `New subscriber opted in to executive briefs:\nEmail: ${newsletterEmail}\nDate: ${new Date().toLocaleString()}`,
      customerEmail: newsletterEmail.trim(),
      customerName: 'Newsletter Subscriber',
    });
  };

  return (
    <footer className="bg-[#05080c] text-white border-t border-slate-800">
      {/* Top Newsletter & Mission Band */}
      <div className="border-b border-slate-800/80 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            <div className="lg:col-span-6 text-left space-y-3">
              <div className="inline-block p-2 rounded-xl bg-white shadow-sm">
                <img
                  src="/thinkers-arcade-logo.webp"
                  alt="Thinkers Arcade - Web & Software Engineering Nairobi Kenya"
                  title="Thinkers Arcade - Web & Software Engineering Nairobi Kenya"
                  width="160"
                  height="40"
                  className="h-9 w-auto object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/logo.png';
                  }}
                />
              </div>
              <p className="text-sm text-slate-400 max-w-lg leading-relaxed">
                Thinkers Arcade builds digital infrastructure for ambitious businesses. We design, develop and scale mobile apps, software systems, AI pipelines, custom ERPs, and high-performance commerce platforms.
              </p>
              <div className="pt-2 text-xs font-mono text-[#b88e4c]">
                Direct Lipa na M-Pesa: <strong>Paybill 542542</strong> · <strong>Account 01550</strong> (I&amp;M Bank)
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-slate-900/90 border border-slate-800 p-6 rounded-2xl text-left">
                <div className="text-xs font-bold uppercase tracking-wider text-[#b88e4c] mb-1 font-mono">
                  Stay Updated
                </div>
                <div className="text-sm font-semibold text-white mb-3">
                  Executive dispatches on African tech, SEO &amp; automation
                </div>

                {newsletterSubscribed ? (
                  <div className="flex items-center gap-2 text-xs text-[#b88e4c] bg-[#b88e4c]/10 p-3 rounded-xl border border-[#b88e4c]/30">
                    <CheckCircle className="h-4 w-4 shrink-0 text-[#b88e4c]" />
                    <span>Thank you for subscribing! We share monthly tactical briefs.</span>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2">
                    <label htmlFor="footer-newsletter-email" className="sr-only">
                      Work email address
                    </label>
                    <input
                      id="footer-newsletter-email"
                      name="footer-newsletter-email"
                      type="email"
                      required
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      placeholder="Enter your work email (Required)"
                      className="flex-1 rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-[#b88e4c]"
                    />
                    <button
                      type="submit"
                      className="rounded-xl bg-[#b88e4c] hover:bg-[#a37a36] text-white font-bold px-5 py-2.5 text-xs transition-colors cursor-pointer whitespace-nowrap shadow-sm min-h-[44px]"
                    >
                      Subscribe
                    </button>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Main Footer Links Columns */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="sr-only">Footer Directory &amp; Navigation</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 text-left">
            
            {/* Solutions */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-300 font-display mb-4">
                Solutions
              </h3>
              <ul className="space-y-2.5 text-xs text-slate-400">
                <li>
                  <button onClick={() => onNavigate('app-development')} className="hover:text-[#009fe3] font-semibold transition-colors flex items-center gap-1 text-white">
                    <span>App Development</span>
                    <span className="text-xs bg-[#009fe3]/15 text-[#009fe3] px-1 py-0.2 rounded border border-[#009fe3]/30 font-mono">Mobile</span>
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('custom-erps')} className="hover:text-[#b88e4c] font-semibold transition-colors flex items-center gap-1 text-[#b88e4c]">
                    <span>Custom ERPs &amp; Excel</span>
                    <span className="text-xs bg-[#b88e4c]/15 text-[#b88e4c] px-1 py-0.2 rounded border border-[#b88e4c]/30 font-mono">New</span>
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('website-development')} className="hover:text-white transition-colors">
                    Website Development
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('app-development')} className="hover:text-white transition-colors">
                    Software Development
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('ai-solutions')} className="hover:text-white transition-colors">
                    AI Systems &amp; RAG
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('seo-growth')} className="hover:text-white transition-colors">
                    SEO &amp; Search Infra
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('cybersecurity')} className="hover:text-white transition-colors">
                    Cybersecurity &amp; ODPC
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('hosting')} className="hover:text-white transition-colors">
                    NVMe Cloud Hosting
                  </button>
                </li>
              </ul>
            </div>

            {/* Industries */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-300 font-display mb-4">
                Industries
              </h3>
              <ul className="space-y-2.5 text-xs text-slate-400">
                <li>
                  <button onClick={() => onNavigate('industries', 'healthcare')} className="hover:text-white transition-colors">
                    Healthcare
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('industries', 'hospitality')} className="hover:text-white transition-colors">
                    Hospitality
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('industries', 'education')} className="hover:text-white transition-colors">
                    Education
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('industries', 'ngos')} className="hover:text-white transition-colors">
                    NGOs &amp; Aid
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('industries', 'churches')} className="hover:text-white transition-colors">
                    Churches
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('industries', 'corporate')} className="hover:text-white transition-colors">
                    Corporate &amp; Legal
                  </button>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-300 font-display mb-4">
                Company &amp; Verticals
              </h3>
              <ul className="space-y-2.5 text-xs text-slate-400">
                <li>
                  <button onClick={() => onNavigate('home')} className="hover:text-white transition-colors cursor-pointer">
                    About Thinkers Arcade
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('solutions')} className="hover:text-[#009fe3] font-semibold transition-colors flex items-center gap-1 text-white cursor-pointer">
                    <span>Solutions Directory</span>
                    <span className="text-[10px] bg-[#009fe3]/15 text-[#009fe3] px-1 py-0.2 rounded border border-[#009fe3]/30 font-mono">Hub</span>
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('venture-lab')} className="hover:text-purple-300 font-semibold transition-colors flex items-center gap-1 text-purple-300 cursor-pointer">
                    <span>Venture Lab (Startup Studio)</span>
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('case-studies')} className="hover:text-white transition-colors cursor-pointer">
                    Case Studies &amp; Snippets
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('store')} className="hover:text-white transition-colors cursor-pointer">
                    Store (POS, Solar, IT)
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('insights')} className="hover:text-white transition-colors cursor-pointer">
                    Insights &amp; Articles
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('pricing')} className="hover:text-white transition-colors cursor-pointer">
                    Pricing Matrix
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('contact')} className="hover:text-white transition-colors cursor-pointer">
                    Contact &amp; Support
                  </button>
                </li>
              </ul>
            </div>

            {/* Kenyan Regional Hubs */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#fcd34d] font-display mb-4">
                Kenyan Regional Hubs
              </h3>
              <ul className="space-y-2.5 text-xs text-slate-400">
                <li>
                  <button onClick={() => onNavigate('kenya-nairobi')} className="hover:text-white transition-colors cursor-pointer flex items-center gap-1.5">
                    <span className="text-emerald-400">●</span> Nairobi HQ
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('kenya-mombasa')} className="hover:text-white transition-colors cursor-pointer flex items-center gap-1.5">
                    <span className="text-blue-400">●</span> Mombasa &amp; Coast
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('kenya-nakuru')} className="hover:text-white transition-colors cursor-pointer flex items-center gap-1.5">
                    <span className="text-amber-400">●</span> Nakuru &amp; Rift Valley
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('kenya-kisumu')} className="hover:text-white transition-colors cursor-pointer flex items-center gap-1.5">
                    <span className="text-cyan-400">●</span> Kisumu &amp; Nyanza
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('kenya-meru')} className="hover:text-white transition-colors cursor-pointer flex items-center gap-1.5">
                    <span className="text-purple-400">●</span> Meru &amp; Mt. Kenya
                  </button>
                </li>
              </ul>
            </div>

            {/* Hosting */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-300 font-display mb-4">
                Hosting &amp; Cloud
              </h3>
              <ul className="space-y-2.5 text-xs text-slate-400">
                <li>
                  <button onClick={() => onNavigate('hosting')} className="hover:text-white transition-colors cursor-pointer">
                    NVMe LiteSpeed Hosting
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('domains')} className="hover:text-white transition-colors cursor-pointer">
                    Domain Registration (.co.ke)
                  </button>
                </li>
                <li>
                  <button onClick={onOpenPortal} className="hover:text-white transition-colors flex items-center gap-1 cursor-pointer">
                    <span>Client Portal</span>
                    <ExternalLink className="h-3 w-3 text-[#009fe3]" />
                  </button>
                </li>
                <li>
                  <button onClick={() => onNavigate('pricing')} className="hover:text-white transition-colors cursor-pointer">
                    Pricing Plans
                  </button>
                </li>
                <li>
                  <a
                    href="https://kenic.or.ke"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-slate-200 transition-colors flex items-center gap-1 text-slate-400 font-mono text-[11px]"
                  >
                    <span>KeNIC Registry (.ke)</span>
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.litespeedtech.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-slate-200 transition-colors flex items-center gap-1 text-slate-400 font-mono text-[11px]"
                  >
                    <span>LiteSpeed Tech Partner</span>
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Direct Contacts */}
            <div className="col-span-2 sm:col-span-1">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-300 font-display mb-4">
                Headquarters
              </h3>
              <div className="space-y-3 text-xs text-slate-400">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-[#009fe3] shrink-0 mt-0.5" />
                  <span>Nairobi, Kenya</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-[#009fe3] shrink-0" />
                  <a href={`tel:${SITE_CONTACT.phone}`} className="hover:text-white">
                    {SITE_CONTACT.phoneFormatted}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-[#009fe3] shrink-0" />
                  <a href={`mailto:${SITE_CONTACT.email}`} className="hover:text-white">
                    {SITE_CONTACT.email}
                  </a>
                </div>
                <div className="pt-2">
                  <button
                    onClick={onOpenBooking}
                    className="w-full py-2 px-3 rounded-lg bg-slate-800 hover:bg-[#b88e4c] text-white text-xs font-bold transition-colors cursor-pointer text-center font-sans shadow-xs"
                  >
                    Book Diagnostic
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Authoritative Ecosystem & Verified Outgoing Outbound Citations (Fixes 'No Outgoing Links' SEO flag) */}
      <div className="border-t border-slate-900 bg-slate-950/80 py-8 text-xs text-slate-400">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 text-left">
            <div>
              <div className="text-[11px] font-mono font-bold uppercase tracking-wider text-[#b88e4c] mb-1">
                Authoritative Integrations &amp; Infrastructure Ecosystem
              </div>
              <p className="text-slate-400 text-xs max-w-xl">
                Certified integrations across East African regulatory rails, payment gateways, and telecommunications peering points.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 font-mono text-[11px]">
              <a
                href="https://developer.safaricom.co.ke"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1 rounded-md bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-[#009fe3] transition-colors flex items-center gap-1"
                title="Safaricom Daraja API Portal"
              >
                <span>Safaricom Daraja 2.0</span>
                <ExternalLink className="h-2.5 w-2.5 text-[#009fe3]" />
              </a>
              <a
                href="https://etims.kra.go.ke"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1 rounded-md bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-[#b88e4c] transition-colors flex items-center gap-1"
                title="Kenya Revenue Authority eTIMS Fiscal System"
              >
                <span>KRA eTIMS API</span>
                <ExternalLink className="h-2.5 w-2.5 text-[#b88e4c]" />
              </a>
              <a
                href="https://www.odpc.go.ke"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1 rounded-md bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-[#009fe3] transition-colors flex items-center gap-1"
                title="Office of the Data Protection Commissioner Kenya"
              >
                <span>ODPC Kenya</span>
                <ExternalLink className="h-2.5 w-2.5 text-[#009fe3]" />
              </a>
              <a
                href="https://web.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1 rounded-md bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-emerald-400 transition-colors flex items-center gap-1"
                title="Google Core Web Vitals"
              >
                <span>Core Web Vitals</span>
                <ExternalLink className="h-2.5 w-2.5 text-emerald-400" />
              </a>
              <a
                href="https://jaymaxauto.co.ke"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1 rounded-md bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-slate-600 transition-colors flex items-center gap-1"
                title="Verified Client Website: Jaymax Auto Kenya"
              >
                <span>Jaymax Auto</span>
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
              <a
                href="https://shantisdesigns.co.ke"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1 rounded-md bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-slate-600 transition-colors flex items-center gap-1"
                title="Verified Client Website: Shantis Designs"
              >
                <span>Shantis Designs</span>
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Legal & Copyright Bar */}
      <div className="border-t border-slate-900 bg-black/60 py-6 text-xs text-slate-500">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            © 2026 Thinkers Arcade Technologies. All rights reserved.
          </div>
          <div className="flex items-center gap-6">
            <a href={`mailto:${SITE_CONTACT.email}`} className="hover:text-slate-300">
              {SITE_CONTACT.email}
            </a>
            <span aria-hidden="true" className="text-slate-700">·</span>
            <span className="font-mono text-slate-400">developed for the african market to the world</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
